import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Checkbox,
  Button,
  FormControlLabel,
  IconButton,
  Box,
  Select,
  MenuItem,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import CloseIcon from '@material-ui/icons/Close';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import DeleteIcon from '@material-ui/icons/Delete';
import usePremiumBacklink from '../hooks/usePremiumBacklink';

const OrderModal = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { 
    modalOpen, 
    closeModal, 
    cart, 
    updateCartItemQuantity, 
    removeFromCart,
    confirmOrder, 
    isCreatingOrder, 
    orderError,
    openTermsModal
  } = usePremiumBacklink();
  
  const [checked, setChecked] = useState(false);

  // Reset checkbox when modal closes
  React.useEffect(() => {
    if (!modalOpen) {
      setChecked(false);
    }
  }, [modalOpen]);

  // Handle quantity change for a cart item
  const handleQtyChange = (itemKey, newQty) => {
    if (updateCartItemQuantity) {
      updateCartItemQuantity(itemKey, newQty);
    }
  };



  // Calculate subtotal and total (add tax/fee if needed)
  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  // Example: add 3.5% fee
  const fee = subtotal * 0;
  const totalDue = subtotal + fee;

  return (
    <Dialog 
      open={modalOpen} 
      onClose={closeModal} 
      maxWidth="md" 
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle disableTypography style={{ 
        display: 'flex', 
        alignItems: 'center', 
        borderBottom: '1px solid #e0e0e0', 
        paddingBottom: 8,
        padding: isMobile ? '12px 16px' : '16px 24px'
      }}>
        <Box display="flex" alignItems="center" mr={2}>
          <ShoppingCartIcon style={{ fontSize: isMobile ? 24 : 32, marginRight: 8 }} />
          <Typography variant={isMobile ? "subtitle1" : "h6"}>Your Cart</Typography>
        </Box>
        <IconButton onClick={closeModal} size="small" style={{ marginLeft: 'auto' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent 
        style={{ 
          padding: isMobile ? '16px 12px' : '20px 24px',
          overflowY: 'auto',
          maxHeight: isMobile ? 'calc(100vh - 200px)' : '60vh'
        }}
      >
        {/* Cart Items */}
        <Box mb={2} p={0}>
          {cart.map((item) => (
            <Box key={item.id} p={1}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                <Typography style={{ fontSize: '0.9rem', fontWeight: 400, color: '#000' }}>
                  {item.name}
                </Typography>
                <Typography style={{ fontSize: '0.9rem', fontWeight: 600, color: '#000' }}>
                  ${(item.price * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap="8px">
                  <Select
                    value={item.quantity}
                    onChange={e => handleQtyChange(item.id, e.target.value)}
                    size="small"
                    style={{ 
                      minWidth: 70,
                      backgroundColor: '#f5f5f5',
                      borderRadius: 4,
                      fontSize: '0.75rem'
                    }}
                  >
                    {[...Array(20).keys()].map(i => (
                      <MenuItem key={i+1} value={i+1}>Qty {i+1}</MenuItem>
                    ))}
                  </Select>
                  <IconButton
                    onClick={() => removeFromCart(item.id)}
                    size="small"
                    style={{ 
                      padding: '4px', 
                      color: '#666',
                      '&:hover': {
                        color: '#f44336',
                        backgroundColor: 'rgba(244, 67, 54, 0.04)'
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography style={{ color: '#666', fontSize: '0.75rem' }}>
                  ${item.price.toLocaleString()} each
                </Typography>
              </Box>
            </Box>
          ))}
          
          {/* Subtotal and Total */}
          <Box px={1} pb={1}>
            <Box borderTop={1} borderColor="#ccc" pt={1}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography style={{ fontSize: '0.9rem', fontWeight: 600, color: '#000' }}>
                  Subtotal
                </Typography>
                <Typography style={{ fontSize: '0.9rem', fontWeight: 600, color: '#000' }}>
                  ${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box borderTop={1} borderColor="#ccc" pt={0.5} mt={0.5}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography style={{ fontSize: '0.9rem', fontWeight: 600, color: '#000' }}>
                    Total due
                  </Typography>
                  <Typography style={{ fontSize: '0.9rem', fontWeight: 600, color: '#000' }}>
                    ${totalDue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        {/* Terms Section */}
        <Box mb={2}>
          <Typography variant="h6" gutterBottom style={{ fontWeight: 600, color: '#333' }}>
            Order Summary
          </Typography>
          <Typography gutterBottom style={{ color: '#666', lineHeight: 1.6 }}>
            Thank you for your order totaling <strong style={{ color: '#1976d2' }}>${subtotal.toLocaleString()}</strong>. 
            Please review and agree to the terms below before we proceed.
          </Typography>
        </Box>
        
        <Box bgcolor="#f8f9fa" borderRadius={8} p={3} mb={3} border={1} borderColor="#e9ecef">
          <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, color: '#495057', marginBottom: 16 }}>
            Terms & Conditions
          </Typography>
          <Box component="ul" style={{ margin: 0, paddingLeft: 20, color: '#6c757d' }}>
            <Box component="li" mb={1} style={{ lineHeight: 1.6 }}>
              You are purchasing <strong>premium backlinks</strong> as outlined in your order. 
              Total amount: <strong style={{ color: '#1976d2' }}>${subtotal.toLocaleString()}</strong>
            </Box>
            <Box component="li" mb={1} style={{ lineHeight: 1.6 }}>
              <strong>No payment</strong> will be processed immediately
            </Box>
            <Box component="li" mb={1} style={{ lineHeight: 1.6 }}>
              Your payment method will only be charged <strong>after the link is live and verified</strong>
            </Box>
            <Box component="li" mb={1} style={{ lineHeight: 1.6 }}>
              Backlink placement may take <strong>2–4 weeks depending on your publication</strong>
            </Box>
            <Box component="li" mb={1} style={{ lineHeight: 1.6 }}>
              You will receive confirmation once the link is live and billing is completed
            </Box>
            <Box component="li" style={{ lineHeight: 1.6 }}>
              Please review the complete{' '}
              <button 
                onClick={openTermsModal}
                type="button"
                style={{ 
                  color: '#1976d2', 
                  textDecoration: 'none', 
                  fontWeight: 500,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  fontFamily: 'inherit',
                  fontSize: 'inherit'
                }}
              >
                Terms & Conditions
              </button>
            </Box>
          </Box>
        </Box>
        
        <Box bgcolor="#e3f2fd" borderRadius={8} p={2} mb={2}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={checked} 
                onChange={e => setChecked(e.target.checked)} 
                color="primary"
                style={{ padding: 8 }}
                disabled={isCreatingOrder}
              />
            }
            label={
              <button
                onClick={openTermsModal}
                type="button"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  fontWeight: 500,
                  color: '#1976d2',
                  textAlign: 'left',
                  lineHeight: 1.4
                }}
              >
                I agree to the above billing terms and authorize payment once the link is live
              </button>
            }
          />
        </Box>
        
        {/* Error Alert */}
        {orderError && (
          <Box mb={2}>
            <Alert severity="error" variant="outlined">
              {orderError}
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ 
        padding: isMobile ? '12px 16px' : '16px 24px', 
        borderTop: '1px solid #e0e0e0', 
        backgroundColor: '#fafafa',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '8px' : '0',
        position: 'sticky',
        bottom: 0,
        zIndex: 1
      }}>
        <Button
          onClick={closeModal}
          color="default"
          variant="outlined"
          disabled={isCreatingOrder}
          style={{ 
            marginRight: isMobile ? 0 : 12,
            minWidth: isMobile ? '100%' : 100,
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={confirmOrder}
          color="primary"
          variant="contained"
          disabled={!checked || cart.length === 0 || isCreatingOrder}
          style={{
            minWidth: isMobile ? '100%' : 140,
            textTransform: 'none',
            fontWeight: 600,
            color: '#fff',
            backgroundColor: (!checked || cart.length === 0 || isCreatingOrder) ? '#ccc' : '#4caf50',
            boxShadow: (!checked || cart.length === 0 || isCreatingOrder) ? 'none' : '0 2px 8px rgba(76, 175, 80, 0.3)',
            '&:hover': {
              backgroundColor: (!checked || cart.length === 0 || isCreatingOrder) ? '#ccc' : '#45a049'
            }
          }}
        >
          {isCreatingOrder && (
            <>
              <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} />
              {isMobile ? 'Creating...' : 'Creating Order...'}
            </>
          )}
          {!isCreatingOrder && (isMobile ? 'Confirm' : 'Confirm Order')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderModal; 